import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faGlobe,
  faSpinner,
  faTriangleExclamation,
  faDatabase,
  faClock,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import StoreHeader from '../../components/StoreHeader';
import Footer from '../../components/Footer';
import { useCurrency } from '../../context/CurrencyContext';
import { formatPrice, pickPrice } from '../../utils/currency';
import { formatData, formatValidity } from '../../utils/formatters';
import { getEsimDetail } from './homeApi';
import './home.css';

// Ürün görseli — yüklenemezse globe ikonuna düşer (bazı ürünlerin img'i yok ya
// da keepgo CDN'inden 404 dönüyor).
const ProductImage = ({ src, alt }) => {
  const [failed, setFailed] = useState(!src);
  if (failed) {
    return (
      <span className="detail-product-img detail-product-img--fallback">
        <FontAwesomeIcon icon={faGlobe} />
      </span>
    );
  }
  return (
    <img
      className="detail-product-img"
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
};

const EsimDetail = () => {
  const { t } = useTranslation();
  const { currency } = useCurrency();
  const { type, id } = useParams();
  const location = useLocation();

  // Ülke rotasında id zaten ülke adıdır ("Germany"), başlık olarak kullanılabilir.
  // Bölge/ömürlük rotasında id sayısal bir kıta id'sidir — başlıkta "3" yazmasın.
  const isRegion = type !== 'country';
  const fallbackName =
    location.state?.name || (isRegion ? '' : decodeURIComponent(id || ''));
  const fallbackFlag = location.state?.flag || null;

  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true });
    getEsimDetail(type, id)
      .then((detail) => !cancelled && setState({ detail }))
      .catch((err) => !cancelled && setState({ error: err.message }));
    return () => {
      cancelled = true;
    };
  }, [type, id]);

  const detail = state.detail;
  const name = detail?.name || fallbackName;
  const flag = detail?.flag || fallbackFlag;
  const products = detail?.products || [];
  const planCount = products.reduce((n, p) => n + (p.refills?.length || 0), 0);

  return (
    <div className="home">
      <StoreHeader />

      <section className="home-catalog detail">
        <Link to="/" className="esim-detail-back">
          <FontAwesomeIcon icon={faArrowLeft} />
          {t('home.detail.back')}
        </Link>

        {/* Başlık */}
        <header className="detail-head">
          {flag ? (
            <img className="detail-flag" src={flag} alt="" />
          ) : (
            <span className="detail-flag detail-flag--fallback">
              <FontAwesomeIcon icon={faGlobe} />
            </span>
          )}
          <div>
            <h1 className="detail-title">{name}</h1>
            {!state.loading && !state.error && planCount > 0 && (
              <p className="detail-sub">{t('detail.count', { count: planCount })}</p>
            )}
          </div>
        </header>

        {state.loading && (
          <div className="home-state">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          </div>
        )}

        {state.error && (
          <div className="home-state">
            <FontAwesomeIcon icon={faTriangleExclamation} size="2x" />
            <p>{t('home.error.title')}</p>
          </div>
        )}

        {!state.loading && !state.error && planCount === 0 && (
          <div className="home-state">
            <p>{t('detail.empty')}</p>
          </div>
        )}

        {!state.loading && !state.error && planCount > 0 && (
          <div className="detail-products">
            {products
              .filter((p) => p.refills?.length)
              .map((product) => (
                <div key={product.originalId} className="detail-product">
                  {/* Bölge/ömürlük sayfalarında birden çok grup (Volans,
                      Lacerta, Norma…) listelenir; her biri kendi görseli ve
                      açıklamasıyla ayrışır. Ülke sayfasında tek grup vardır,
                      orada sadece ad yazılır — görsel/açıklama gereksiz. */}
                  {isRegion ? (
                    <header className="detail-product-head">
                      <ProductImage src={product.img} alt={product.name || ''} />
                      <div>
                        {product.name && (
                          <h2 className="detail-product-name">{product.name}</h2>
                        )}
                        {product.description && (
                          <p className="detail-product-desc">{product.description}</p>
                        )}
                      </div>
                    </header>
                  ) : (
                    product.name && (
                      <h2 className="detail-product-name detail-product-name--solo">
                        {product.name}
                      </h2>
                    )
                  )}
                  <ul className="plan-list">
                    {product.refills.map((r) => {
                      const price = formatPrice(pickPrice(r, currency), currency);
                      return (
                        <li key={`${product.originalId}-${r.id}`}>
                          <Link
                            className="plan"
                            to={`/esim/plan/${product.originalId}/${r.id}`}
                            state={{
                              title: name,
                              // Ülkede bayrak ülkenindir; bölgede grubun görseli.
                              flag: isRegion ? product.img || flag : flag,
                            }}
                          >
                            <span className="plan-data">
                              <FontAwesomeIcon icon={faDatabase} />
                              {formatData(r.amountMb, { empty: '' })}
                            </span>
                            <span className="plan-validity">
                              <FontAwesomeIcon icon={faClock} />
                              {formatValidity(t, r.amountDays)}
                            </span>
                            {price && <span className="plan-price">{price}</span>}
                            <span className="plan-buy">
                              {t('detail.buy')}
                              <FontAwesomeIcon icon={faArrowRight} />
                            </span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default EsimDetail;
